/**
 * Table of contents for blog articles: H2 sections with nested H3s,
 * smooth scroll, sticky sidebar support, and active heading highlight.
 */
const TOC_MEDIA = '(min-width: 990px)';

function slugFromText(text, fallback) {
  const base = text
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);
  return base || `section-${fallback}`;
}

function ensureUniqueId(el, baseId, used) {
  if (el.id && !used.has(el.id)) {
    used.add(el.id);
    return el.id;
  }
  let id = baseId;
  let n = 0;
  while (used.has(id) || document.getElementById(id)) {
    n += 1;
    id = `${baseId}-${n}`;
  }
  el.id = id;
  used.add(id);
  return id;
}

function syncDetailsOpen(panel) {
  if (!panel) return;
  const mq = window.matchMedia(TOC_MEDIA);
  const apply = () => {
    panel.open = mq.matches;
  };
  apply();
  mq.addEventListener('change', apply);
}

function buildTocStructure(formatter) {
  const usedIds = new Set();
  const items = [];
  let current = null;
  let h2Index = 0;
  let h3Index = 0;

  formatter.querySelectorAll('h2, h3').forEach((el) => {
    if (el.tagName === 'H2') {
      const text = el.textContent || '';
      const base = slugFromText(text, String(h2Index));
      const id = ensureUniqueId(el, `article-h2-${base}`, usedIds);
      current = { id, text, h3s: [] };
      items.push(current);
      h2Index += 1;
    } else if (el.tagName === 'H3' && current) {
      const text = el.textContent || '';
      const base = slugFromText(text, `h3-${h3Index}`);
      const id = ensureUniqueId(el, `article-h3-${base}`, usedIds);
      current.h3s.push({ id, text });
      h3Index += 1;
    }
  });

  return items;
}

function renderTocList(items, listEl) {
  listEl.textContent = '';
  const ul = document.createElement('ul');
  ul.className = 'article-with-toc-layout__toc-list';

  for (const item of items) {
    const li = document.createElement('li');
    li.className = 'article-with-toc-layout__toc-item';

    const a = document.createElement('a');
    a.className = 'article-with-toc-layout__toc-link article-with-toc-layout__toc-link--h2';
    a.href = `#${item.id}`;
    a.textContent = item.text;
    li.appendChild(a);

    if (item.h3s.length) {
      const sub = document.createElement('ul');
      sub.className = 'article-with-toc-layout__toc-sub';
      for (const h3 of item.h3s) {
        const li3 = document.createElement('li');
        const a3 = document.createElement('a');
        a3.className = 'article-with-toc-layout__toc-link article-with-toc-layout__toc-link--h3';
        a3.href = `#${h3.id}`;
        a3.textContent = h3.text;
        li3.appendChild(a3);
        sub.appendChild(li3);
      }
      li.appendChild(sub);
    }

    ul.appendChild(li);
  }

  listEl.appendChild(ul);
}

function bindSmoothScroll(listEl) {
  listEl.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a || !listEl.contains(a)) return;
    const id = decodeURIComponent(a.getAttribute('href').slice(1));
    const target = document.getElementById(id);
    if (!target) return;
    e.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', `#${id}`);
  });
}

function bindActiveHeading(items, listEl) {
  const h2Ids = items.map((i) => i.id);
  if (!h2Ids.length) return;

  const links = [...listEl.querySelectorAll('a.article-with-toc-layout__toc-link--h2')];
  const h3Links = [...listEl.querySelectorAll('a.article-with-toc-layout__toc-link--h3')];
  const map = new Map(h2Ids.map((id, idx) => [id, links[idx]]));
  const h3ToH2 = new Map();
  for (const item of items) {
    for (const h3 of item.h3s) {
      h3ToH2.set(h3.id, item.id);
    }
  }

  const elements = h2Ids.map((id) => document.getElementById(id)).filter(Boolean);
  if (!elements.length) return;

  const setActive = (h2Id) => {
    for (const link of links) {
      link.classList.toggle('is-active', link.getAttribute('href') === `#${h2Id}`);
    }
    for (const link of h3Links) {
      link.classList.remove('is-active');
    }
  };

  const io = new IntersectionObserver(
    (entries) => {
      const visible = entries.filter((en) => en.isIntersecting && en.target.id);
      if (!visible.length) return;
      visible.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
      setActive(visible[0].target.id);
    },
    { rootMargin: '-12% 0px -55% 0px', threshold: [0, 0.05, 1] }
  );

  for (const el of elements) io.observe(el);

  const onHash = () => {
    const raw = window.location.hash.slice(1);
    if (!raw) return;
    if (map.has(raw)) {
      setActive(raw);
      return;
    }
    if (h3ToH2.has(raw)) {
      setActive(h3ToH2.get(raw));
      for (const link of h3Links) {
        link.classList.toggle('is-active', link.getAttribute('href') === `#${raw}`);
      }
    }
  };
  window.addEventListener('hashchange', onHash);
  onHash();
}

function initLayout(layout) {
  const rteRoot = layout.querySelector('[data-article-rte-root]');
  const listMount = layout.querySelector('[data-article-toc-list]');
  const panel = layout.querySelector('[data-article-toc-panel]');
  if (!rteRoot || !listMount) return;

  const formatter = rteRoot.querySelector('rte-formatter');
  if (!formatter) return;

  const items = buildTocStructure(formatter);
  if (!items.length) {
    layout.classList.add('article-with-toc-layout--empty');
    if (panel) panel.hidden = true;
    layout.dataset.articleTocInit = '1';
    return;
  }

  layout.classList.remove('article-with-toc-layout--empty');
  if (panel) panel.hidden = false;

  const mq = window.matchMedia(TOC_MEDIA);
  if (!mq.matches) {
    return;
  }

  if (layout.dataset.articleTocInit === '1') return;

  renderTocList(items, listMount);
  bindSmoothScroll(listMount);
  bindActiveHeading(items, listMount);
  syncDetailsOpen(panel);

  layout.dataset.articleTocInit = '1';
}

function init() {
  document.querySelectorAll('[data-article-with-toc-layout]:not([data-article-toc-disabled])').forEach(initLayout);
}

function bindTocViewportInit() {
  const mq = window.matchMedia(TOC_MEDIA);
  mq.addEventListener('change', () => {
    if (!mq.matches) return;
    document.querySelectorAll('[data-article-with-toc-layout]:not([data-article-toc-disabled])').forEach((layout) => {
      if (layout.dataset.articleTocInit === '1') return;
      initLayout(layout);
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => {
      init();
      bindTocViewportInit();
    },
    { once: true }
  );
} else {
  init();
  bindTocViewportInit();
}
